import React, { FC, ReactNode, useMemo } from 'react';
import { deepmerge } from '@mui/utils';
import CssBaseline from '@mui/material/CssBaseline';
import {
  createTheme,
  ThemeOptions,
  ThemeProvider,
} from '@mui/material/styles';
import getThemes, { LAYOUT } from './themes';
import { interFontFamily, typography } from './typography';

interface ThemeWrapperProps {
  children: ReactNode;
  tenantId?: number | null;
  themeMode?: 'light' | 'dark';
}

const ThemeWrapper: FC<ThemeWrapperProps> = ({
  children,
  tenantId,
  themeMode = 'light',
}) => {
  const themeColors = useMemo(() => getThemes(), []);

  const defaultTheme: ThemeOptions = {
    typography,
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => {
          const { primary, secondary } = theme.palette;

          return {
            html: { fontFamily: interFontFamily },
            body: {
              fontFamily: interFontFamily,
              backgroundColor: theme.palette.background.default,
            },
            '*, *::before, *::after': { fontFamily: 'inherit' },
            a: { color: 'inherit' },
            ':root': {
              '--color-brand-primary':
                typeof primary.main === 'string' ? primary.main : '',
              '--color-brand-primary-light':
                typeof primary.light === 'string' ? primary.light : '',
              '--color-brand-primary-dark':
                typeof primary.dark === 'string' ? primary.dark : '',
              '--color-brand-secondary':
                typeof secondary?.main === 'string' ? secondary.main : '',
              '--color-text-primary': theme.palette.text.primary,
              '--color-text-secondary': theme.palette.text.secondary,
              '--color-background-default': theme.palette.background.default,
              '--color-surface-paper': theme.palette.background.paper,
              '--layout-mobile-nav-width': `${LAYOUT.mobileNavWidth}px`,
              '--layout-max-body-width': `${LAYOUT.maxBodyWidth}px`,
            },
          };
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            subtitle1: 'h4',
            subtitle2: 'h5',
            body1: 'p',
            body2: 'p',
            displayLarge: 'h1',
            displayLargeBold: 'h1',
            displayMedium: 'h1',
            displayMediumBold: 'h1',
            displaySmall: 'h1',
            displaySmallBold: 'h1',
            h1Bold: 'h1',
            h2Bold: 'h2',
            h3SemiBold: 'h3',
            h3Bold: 'h3',
            subtitle1SemiBold: 'h4',
            subtitle1Bold: 'h4',
            subtitle2SemiBold: 'h5',
            subtitle2Bold: 'h5',
            body1SemiBold: 'p',
            body1Bold: 'p',
            body1Underline: 'p',
            body1BoldUnderline: 'p',
            body1Italic: 'p',
            body2SemiBold: 'p',
            body2Bold: 'p',
            body2Underline: 'p',
            body2BoldUnderline: 'p',
            body2Italic: 'p',
            caption1: 'span',
            caption1Bold: 'span',
            caption1Italic: 'span',
            caption2: 'span',
            caption2Bold: 'span',
            bodyError: 'p',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
              {
                WebkitAppearance: 'none',
                margin: 0,
              },
            'input[type=number]': { MozAppearance: 'textfield' },
            '& .MuiInputLabel-root': { color: theme.palette.text.primary },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { lineBreak: 'anywhere' },
          head: {
            whiteSpace: 'nowrap',
            textTransform: 'capitalize',
            fontWeight: 'bold',
          },
        },
      },
    },
  };

  const selectedTheme =
    themeColors[Number(tenantId) as keyof typeof themeColors] || themeColors[3];

  const theme = useMemo(() => {
    const palette =
      selectedTheme.colorSchemes?.[themeMode]?.palette ||
      selectedTheme.colorSchemes?.light?.palette ||
      {};
    return createTheme(
      deepmerge(defaultTheme, {
        palette: { mode: themeMode, ...palette },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default ThemeWrapper;
